import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { RecentActivity } from '@redpanda/types';
import { getActivities } from '../services/api';

interface ActivityState {
  activities: RecentActivity[]; // 當前頁面顯示的活動
  allActivities: Map<number, RecentActivity[]>; // 所有頁面的快取資料
  pageCache: Map<number, number>; // 每頁的載入時間戳
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  total: number;
  itemsPerPage: number;
  lastFetch: number | null;
}

interface ActivityActions {
  fetchActivities: (page?: number) => Promise<void>;
  setCurrentPage: (page: number) => void;
  invalidateCache: () => void;
}

export const ACTIVITIES_PER_PAGE = 20;

export const useActivityStore = create<ActivityState & ActivityActions>()(
  persist(
    (set, get) => ({
  activities: [],
  allActivities: new Map(),
  pageCache: new Map(),
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  total: 0,
  itemsPerPage: ACTIVITIES_PER_PAGE,
  lastFetch: null,

  fetchActivities: async (page = 1) => {
    const state = get();
    const now = Date.now();
    
    // 檢查該頁是否已有快取且仍然有效
    const pageLastFetch = state.pageCache.get(page);
    const hasValidCache = pageLastFetch; // 快取永久有效
    const cachedPageData = state.allActivities.get(page);
    
    // 如果有有效快取，直接使用
    if (hasValidCache && cachedPageData && cachedPageData.length > 0) {
      set({
        activities: cachedPageData,
        currentPage: page,
        loading: false,
        error: null
      });
      return;
    }
    
    // 如果是第一頁，先嘗試從儀表板同步最新資料
    if (page === 1 && !pageLastFetch) { // 只在沒有快取時載入
      try {
        const { useDashboardStore } = await import('./dashboardStore');
        const dashboardState = useDashboardStore.getState();
        
        // 如果儀表板有最新資料，先使用這些資料
        if (dashboardState.stats?.recentActivities && dashboardState.stats.recentActivities.length > 0) {
          const newAllActivities = new Map(state.allActivities);
          const newPageCache = new Map(state.pageCache);
          
          // 快取第一頁的儀表板資料
          newAllActivities.set(1, dashboardState.stats.recentActivities);
          newPageCache.set(1, now);
          
          set({
            activities: dashboardState.stats.recentActivities,
            allActivities: newAllActivities,
            pageCache: newPageCache,
            currentPage: 1,
            loading: false,
            error: null
          });
          
          // 在背景載入完整的第一頁資料來獲取正確的分頁資訊，但不顯示載入畫面
          try {
            const response = await getActivities(1, ACTIVITIES_PER_PAGE);
            if (response?.data?.data) {
              const fullPageData = response.data.data;
              const total = response.data.pagination.totalItems;
              
              // 更新完整的第一頁資料和分頁資訊
              const updatedAllActivities = new Map(newAllActivities);
              const updatedPageCache = new Map(newPageCache);
              updatedAllActivities.set(1, fullPageData);
              updatedPageCache.set(1, now);
              
              set({
                activities: fullPageData, // 使用完整的20筆資料
                allActivities: updatedAllActivities,
                pageCache: updatedPageCache,
                totalPages: Math.ceil(total / ACTIVITIES_PER_PAGE),
                total,
                lastFetch: now,
              });
            }
          } catch (bgError) {
            // 背景載入失敗，保持使用儀表板資料
            console.warn('Background loading failed, using dashboard data:', bgError);
          }
          return; // 儀表板同步完成，結束函數
        }
      } catch (error) {
        // 如果同步儀表板失敗，繼續正常的 API 請求
      }
    }

    // 只有在沒有任何快取資料時才顯示載入畫面
    const hasAnyData = state.allActivities.size > 0;
    set({ 
      loading: !hasAnyData, 
      error: null, 
      currentPage: page 
    });

    try {
      const response = await getActivities(page, ACTIVITIES_PER_PAGE);

      if (response?.data?.data) {
        const newActivities = response.data.data;
        const total = response.data.pagination.totalItems;
        
        // 更新快取
        const newAllActivities = new Map(state.allActivities);
        const newPageCache = new Map(state.pageCache);
        newAllActivities.set(page, newActivities);
        newPageCache.set(page, now);
        
        set({
          activities: newActivities,
          allActivities: newAllActivities,
          pageCache: newPageCache,
          totalPages: Math.ceil(total / ACTIVITIES_PER_PAGE),
          total,
          lastFetch: now,
          loading: false,
          error: null
        });
      } else {
        set({
          activities: [],
          totalPages: 1,
          total: 0,
          lastFetch: now,
          loading: false,
          error: null
        });
      }
    } catch (err: any) {
      console.error('Error fetching activities:', err);
      set({
        loading: false,
        error: err.response?.data?.error || '無法載入動態記錄'
      });
    }
  },

  setCurrentPage: (page: number) => {
    const state = get();
    
    // 檢查目標頁面是否有有效快取
    const pageLastFetch = state.pageCache.get(page);
    const hasValidCache = pageLastFetch; // 快取永久有效
    const cachedPageData = state.allActivities.get(page);
    
    // 換頁時自動捲到頂部
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    if (hasValidCache && cachedPageData && cachedPageData.length > 0) {
      // 有快取，立即切換
      set({
        currentPage: page,
        activities: cachedPageData,
        loading: false,
        error: null
      });
    } else {
      // 沒有快取，需要載入
      set({ currentPage: page });
      get().fetchActivities(page);
    }
  },

  invalidateCache: () => {
    // 清除所有快取，強制重新載入
    set({ 
      allActivities: new Map(),
      pageCache: new Map(),
      lastFetch: null 
    });
  }
    }),
    {
      name: 'activity-store',
      partialize: (state) => ({
        activities: state.activities,
        allActivities: Array.from(state.allActivities.entries()), // 轉換 Map 為 Array 以便序列化
        pageCache: Array.from(state.pageCache.entries()), // 轉換 Map 為 Array 以便序列化
        currentPage: state.currentPage,
        totalPages: state.totalPages,
        total: state.total,
        lastFetch: state.lastFetch
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // 重新載入時將 Array 轉換回 Map
          state.allActivities = new Map(state.allActivities as any);
          state.pageCache = new Map(state.pageCache as any);
        }
      }
    }
  )
);